import { GameEngine, GameEvent, clone, err } from "../engine.js";
import { RngState, shuffle } from "../rng.js";

/**
 * Memory pairs (ذاكرة) — 1–4 players take turns flipping two cards.
 * A match scores a point and grants another turn. Faces stay hidden on the
 * server until flipped, so clients cannot read the layout.
 */
export interface MemoryOptions {
  pairs: number;
}
export interface MemoryState {
  n: number;
  faces: number[];
  owner: number[]; // -1 unmatched, else seat
  flipped: number[];
  lastMismatch: number[];
  turn: number;
  flips: number;
  scores: number[];
  over: boolean;
  options: MemoryOptions;
  rng: RngState;
}
export type MemoryMove = { type: "flip"; index: number };
export interface MemoryView {
  n: number;
  cards: (number | null)[];
  owner: number[];
  flipped: number[];
  lastMismatch: number[];
  turn: number;
  scores: number[];
  over: boolean;
}

export const memory: GameEngine<MemoryState, MemoryMove, MemoryView, MemoryOptions> = {
  id: "memory",
  minPlayers: 1,
  maxPlayers: 4,
  playerCounts: [1, 2, 3, 4],
  defaultOptions: { pairs: 12 },

  init(n, seed, options) {
    const opts = { ...this.defaultOptions, ...options };
    const pairs = Math.min(Math.max(opts.pairs | 0, 4), 24);
    const rng = { s: seed | 0 };
    const faces = shuffle(rng, Array.from({ length: pairs * 2 }, (_, i) => Math.floor(i / 2)));
    return {
      n,
      faces,
      owner: faces.map(() => -1),
      flipped: [],
      lastMismatch: [],
      turn: 0,
      flips: 0,
      scores: Array(n).fill(0),
      over: false,
      options: { pairs },
      rng,
    };
  },

  currentSeat: (s) => (s.over ? null : s.turn),

  applyMove(state, seat, move) {
    if (state.over) return err("game_over");
    if (seat !== state.turn) return err("not_your_turn");
    if (move?.type !== "flip") return err("unknown_move");
    const i = move.index;
    if (!Number.isInteger(i) || i < 0 || i >= state.faces.length) return err("bad_index");
    if (state.owner[i] !== -1 || state.flipped.includes(i)) return err("already_open");
    const s = clone(state);
    const ev: GameEvent[] = [];
    s.lastMismatch = [];
    s.flipped.push(i);
    s.flips++;
    ev.push({ type: "flipped", seat, index: i, face: s.faces[i] });
    if (s.flipped.length === 2) {
      const [a, b] = s.flipped;
      if (s.faces[a] === s.faces[b]) {
        s.owner[a] = s.owner[b] = seat;
        s.scores[seat]++;
        ev.push({ type: "matched", seat, a, b });
        if (s.owner.every((o) => o !== -1)) s.over = true;
      } else {
        s.lastMismatch = [a, b];
        s.turn = (seat + 1) % s.n;
        ev.push({ type: "mismatch", seat, a, b });
      }
      s.flipped = [];
    }
    return { ok: true, state: s, events: ev };
  },

  view(s) {
    const shown = new Set([...s.flipped, ...s.lastMismatch]);
    return {
      n: s.n,
      cards: s.faces.map((f, i) => (s.owner[i] !== -1 || shown.has(i) ? f : null)),
      owner: s.owner.slice(),
      flipped: s.flipped.slice(),
      lastMismatch: s.lastMismatch.slice(),
      turn: s.turn,
      scores: s.scores.slice(),
      over: s.over,
    };
  },

  result(s) {
    if (!s.over) return null;
    const vals = [...new Set(s.scores)].sort((a, b) => b - a);
    return {
      ranking: vals.map((v) => s.scores.map((x, i) => (x === v ? i : -1)).filter((i) => i >= 0)),
      scores: s.scores.slice(),
      reason: "all_pairs_found",
    };
  },

  autoMove(s, seat) {
    if (s.over || s.turn !== seat) return null;
    // Timeout fallback: a blind (non-cheating) pick that varies over time.
    const open = s.owner.map((o, idx) => idx).filter((idx) => s.owner[idx] === -1 && !s.flipped.includes(idx));
    if (!open.length) return null;
    return { type: "flip", index: open[(s.flips * s.flips * 5 + s.flips * 3 + 1) % open.length] };
  },
};
